import React from 'react';
import { Modal, Button, Typography } from 'antd';
import { variantTokens } from '../../../theme/theme';

const { Text } = Typography;

// Costanti dei colori
const ERROR = variantTokens.error;
const LIGHT_GREY = variantTokens.lightGrey;

/**
 * FullScreenModal - A reusable full-screen modal component for complex interfaces
 * 
 * @param {boolean} visible - Controls if the modal is visible
 * @param {function} onClose - Function called when modal is closed
 * @param {string} title - Modal title
 * @param {React.ReactNode} children - Custom content to display in the modal
 * @param {function} onConfirm - Function called when confirm button is clicked
 * @param {string} cancelText - Text for the cancel button
 * @param {string} confirmText - Text for the confirm button
 * @param {string} buttonColor - Color for the confirm button (default: error)
 * @param {string} closeButtonPosition - Position of the close button: 'bottom' (default) or 'top'
 * @param {React.ReactNode} customLeftButton - Custom button to display on the left side of the footer
 * @returns {React.ReactElement}
 */

const FullScreenModal = ({
    visible = false,
    onClose,
    title = "Full Screen Modal",
    children,
    onConfirm,
    cancelText = "Close",
    confirmText = "Confirm",
    buttonColor = ERROR,
    iframe = false,
    closeButtonPosition = 'bottom',
    padding = 'clamp(0.75rem, 3vw, 1.25rem)',
    customLeftButton = null
}) => {
    const handleCancel = () => {
        if (onClose) onClose();
    };

    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        handleCancel();
    };

    // Definisce il bottone di chiusura che può essere posizionato in alto o in basso
    const closeButton = (
        <Button
            key="back"
            onClick={handleCancel}
            style={{
                fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                height: 'auto',
                padding: 'clamp(0.25rem, 1vw, 0.5rem) clamp(0.5rem, 2vw, 1rem)',
                minHeight: 'clamp(2rem, 5vw, 2.25rem)'
            }}
        >
            {cancelText}
        </Button>
    );

    // Crea il footer in base alla posizione del bottone di chiusura
    const footerButtons = closeButtonPosition === 'none' ? [] : closeButtonPosition === 'top' ? [
        !iframe && confirmText && onConfirm && (
            <Button
                key="submit"
                type="primary"
                onClick={handleConfirm}
                style={{
                    backgroundColor: buttonColor,
                    borderColor: buttonColor,
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                    height: 'auto',
                    padding: 'clamp(0.25rem, 1vw, 0.5rem) clamp(0.5rem, 2vw, 1rem)',
                    minHeight: 'clamp(2rem, 5vw, 2.25rem)'
                }}
            >
                {confirmText}
            </Button>
        )
    ].filter(Boolean) : [
        closeButton,
        !iframe && confirmText && onConfirm && (
            <Button
                key="submit"
                type="primary"
                onClick={handleConfirm}
                style={{
                    backgroundColor: buttonColor,
                    borderColor: buttonColor,
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                    height: 'auto',
                    padding: 'clamp(0.25rem, 1vw, 0.5rem) clamp(0.5rem, 2vw, 1rem)',
                    minHeight: 'clamp(2rem, 5vw, 2.25rem)'
                }}
            >
                {confirmText}
            </Button>
        )
    ].filter(Boolean);

    // Se c'è un customLeftButton, lo aggiungiamo al footer
    const finalFooter = customLeftButton ? (
        <div style={{
            display: 'flex',
            justifyContent: footerButtons.length > 0 ? 'space-between' : 'flex-end',
            alignItems: 'center',
            width: '100%'
        }}>
            {footerButtons.length > 0 && (
                <div>
                    {customLeftButton}
                </div>
            )}
            {footerButtons.length === 0 ? (
                customLeftButton
            ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {footerButtons}
                </div>
            )}
        </div>
    ) : footerButtons;

    return (
        <Modal
            title={title}
            open={visible}
            onCancel={handleCancel}
            closeIcon={iframe || closeButtonPosition === 'top' ? false : undefined}
            footer={customLeftButton || footerButtons.length > 0 ? finalFooter : null}
            width='calc(100vw - clamp(5rem, 10vw, 7.25rem))'
            style={{
                marginLeft: 'clamp(3rem, 8vw, 6rem)'
            }}
            styles={{
                header: {
                    padding: 'clamp(0.75rem, 3vw, 1.25rem)',
                    fontSize: 'clamp(1rem, 3vw, 1.25rem)'
                },
                body: {
                    padding: padding,
                    overflowY: 'auto',
                    // Quando il bottone è in alto e non c'è footer, regoliamo l'altezza
                    height: closeButtonPosition === 'top' || (footerButtons.length === 0 && !customLeftButton) ?
                        'calc(100vh - 13.5rem)' : 'calc(100vh - 19.75rem)'
                },
                footer: {
                    padding: 'clamp(0.5rem, 2vw, 1rem) clamp(0.75rem, 3vw, 1.25rem)',
                    textAlign: customLeftButton ? 'left' : 'right',
                    borderTop: '1px solid #f0f0f0'
                }
            }}
        >
            {closeButtonPosition === 'top' && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000
                }}>
                    {closeButton}
                </div>
            )}
            <div style={{ padding: 'clamp(0.75rem, 3vw, 1.25rem) 0' }}>
                {children || (
                    <>
                        <Text style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1rem)' }}>This is a full screen modal.</Text>
                        <p style={{ marginTop: 'clamp(0.5rem, 2vw, 0.9375rem)', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                            Use this type of modal for complex visualizations,
                            dashboards, editors or any content that requires maximum workspace
                            while maintaining the application context.
                        </p>
                        <div style={{
                            height: 'clamp(15rem, 40vh, 25rem)',
                            backgroundColor: LIGHT_GREY,
                            borderRadius: 'clamp(0.125rem, 1vw, 0.25rem)',
                            padding: 'clamp(0.75rem, 3vw, 1.25rem)',
                            marginTop: 'clamp(0.5rem, 2vw, 0.9375rem)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <Text style={{ fontSize: 'clamp(0.875rem, 2.5vw, 1.125rem)' }}>Full screen content area</Text>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default FullScreenModal; 